import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Camera } from 'lucide-react';
import { Link } from 'react-router-dom';

import { PartDetails, PictureItem } from '../../services/types';
import { parseCalendarDate } from '../../utils/calendarDate';

import { PartPicturePanel } from './PartPicturePanel';

interface PartInformationSectionProps {
  partDetails: PartDetails;
  pictures?: PictureItem[] | null;
  picturesLoading?: boolean;
}

export const PartInformationSection: React.FC<PartInformationSectionProps> = ({
  partDetails,
  pictures,
  picturesLoading,
}) => {
  const { t } = useTranslation();

  // Fields to display in the main grid (excluding notes and onHold fields)
  const displayFields = [
    'part',
    'descriptionEN',
    'descriptionFR',
    'ium',
    'typeCode',
    'nonStock',
    'oemSampleLocation',
    'pickingLocation',
    'webPart',
    'minimumQty',
  ];

  return (
    <div className="bg-white rounded border border-gray-300 shadow-sm mb-4">
      {/* Header */}
      <div className="bg-blue-50 px-4 py-3 border-b border-blue-200 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-blue-900">
              {t('parts.partInformation')}
            </h2>
            {partDetails.onHold && (
              <div className="relative group cursor-help">
                <AlertCircle className="w-6 h-6 text-red-600" />
                {/* Tooltip */}
                <div className="hidden group-hover:block absolute left-0 top-full mt-1 bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10 shadow-lg">
                  <p>
                    {t('parts.onHoldSince')}{' '}
                    {formatDate(partDetails.onHoldDate)}
                  </p>
                  <p>
                    {t('parts.onHoldReason')}: {partDetails.onHoldReasonCode}
                  </p>
                </div>
              </div>
            )}
          </div>
          <p className="text-xs text-blue-700">
            {formatPartNumber(partDetails.part, partDetails.erpPartNum)}
          </p>
        </div>
        <Link
          to={`/picture-processor?partNum=${encodeURIComponent(partDetails.erpPartNum)}&autoConfirm=1`}
          className="flex items-center justify-center w-9 h-9 rounded-md text-blue-700 bg-white border border-blue-300 hover:bg-blue-100 transition-colors flex-shrink-0"
          title={t('parts.pictures.addPhoto')}
          aria-label={t('parts.pictures.addPhoto')}
        >
          <Camera className="w-4 h-4" />
        </Link>
      </div>

      {/* Content - Picture | Fields | Notes (Notes hidden below xl) */}
      <div className="p-4 flex flex-col sm:flex-row gap-4">
        {/* Picture panel */}
        <div className="self-center sm:self-start">
          <PartPicturePanel
            pictures={pictures ?? []}
            loading={picturesLoading ?? false}
          />
        </div>

        {/* Field grid */}
        <div className="flex-1 min-w-0">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {displayFields.map((fieldKey) => {
              let displayValue: string;

              // For 'part' field, combine with erpPartNum in format: "PartNumUM (erpPartNum)"
              if (fieldKey === 'part') {
                const partValue = partDetails[fieldKey as keyof PartDetails];
                const erpPartValue = partDetails.erpPartNum;
                displayValue = formatPartNumber(
                  partValue as string,
                  erpPartValue
                );
              } else if (fieldKey === 'minimumQty') {
                displayValue = formatMinimumQty(
                  partDetails.minimumQty,
                  partDetails.minMfgLotSize
                );
              } else {
                const value = partDetails[fieldKey as keyof PartDetails];
                displayValue = formatFieldValue(value);
              }

              return (
                <div key={fieldKey} className="border-b border-gray-100 pb-2">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-tight mb-1 truncate">
                    {t(`parts.fields.${fieldKey}`, fieldKey)}
                  </p>
                  <p className="text-sm font-medium text-gray-900 break-words">
                    {displayValue}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Notes — hidden below xl to avoid crowding */}
        <div className="hidden xl:flex w-80 flex-col border-l border-gray-200 pl-4 max-h-48">
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-tight mb-3">
            {t('parts.fields.notes', 'Notes')}
          </p>
          <div className="flex-1 overflow-y-auto pr-2">
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
              {partDetails.notes || (
                <span className="text-gray-400 italic">No notes</span>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Format part number to display as "PartNumUM (erpPartNum)"
 */
function formatPartNumber(partNumUM: string, erpPartNum: string): string {
  if (!partNumUM || !erpPartNum) {
    return partNumUM || erpPartNum || '-';
  }
  return `${partNumUM} (${erpPartNum})`;
}

/**
 * Format minimum qty as "MinimumQty → MinimumQty + MinMfgLotSize"
 */
function formatMinimumQty(minimumQty: number, minMfgLotSize: number): string {
  if (minMfgLotSize > 0) {
    return `${minimumQty} → ${minimumQty + minMfgLotSize}`;
  }
  return minimumQty.toString();
}

/**
 * Format field values for display
 */
function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '-';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'number') {
    return value.toString();
  }

  const stringValue = String(value).trim();
  return stringValue || '-';
}

/**
 * Format date string for display
 */
function formatDate(dateString: string | null): string {
  if (!dateString) return '-';

  try {
    const date = parseCalendarDate(dateString);

    if (isNaN(date.getTime())) {
      return dateString;
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
}
